import { useConfig } from "$global-providers/config-provider/provider";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useRxCollectionInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import { useRxCollectionPrice } from "$global-providers/rxdb-provider/collections/price";
import { useRxCollectionPriceListFacility } from "$global-providers/rxdb-provider/collections/price-list-facility";
import { useRxCollectionPriceListItemExternalReference } from "$global-providers/rxdb-provider/collections/price-list-item-external-reference";
import { useRxCollectionPriceListTariff } from "$global-providers/rxdb-provider/collections/price-list-tariff";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { Invoice } from "$routes/_lib/billing-v2/invoice";
import { randomUniqueId } from "$routes/_lib/random-id";
import useFacility from "$routes/_lib/use-facility";
import { BillingCurrency, BillingMode, PayerType } from "elephant-web/gql";
import { useCallback, useEffect, useState } from "react";
import { buildLineItem, type ExternalReference } from "./build-line-item";
import { lookupPrice } from "./price-lookup";
import {
  createPendingInvoice,
  findActivePendingInvoice,
} from "./resolve-active-pending-invoice";

export interface EmitLineItemArgs {
  patientId: string;
  externalReference: ExternalReference;
  scopeId: string;
  actorId: string;
  incurredAt?: string;
}

export interface PreServiceBillingApi {
  enabled: boolean;
  emit: (args: EmitLineItemArgs) => Promise<void>;
}

const noopApi: PreServiceBillingApi = {
  enabled: false,
  emit: () => Promise.resolve(),
};

const TERRITORY_TO_BILLING_CURRENCY: Record<string, BillingCurrency> = {
  nigeria: BillingCurrency.Ngn,
  liberia: BillingCurrency.Lrd,
};

const pendingInvoiceOperations = new Map<string, Promise<void>>();

const coordinatePendingInvoice = async (
  key: string,
  operation: () => Promise<void>,
): Promise<void> => {
  const previous = pendingInvoiceOperations.get(key) ?? Promise.resolve();
  const current = previous.catch(() => undefined).then(operation);
  pendingInvoiceOperations.set(key, current);
  try {
    await current;
  } finally {
    if (pendingInvoiceOperations.get(key) === current) {
      pendingInvoiceOperations.delete(key);
    }
  }
};

export const usePreServiceBilling = (): PreServiceBillingApi => {
  const [rxdbBillingEnabled] = useFeatureFlag<boolean>(
    featureFlags.billing.rxdbBilling,
  );
  const { data: facility } = useFacility();
  const { territory } = useConfig();
  const invoiceCollection = useRxCollectionInvoice();
  const priceCollection = useRxCollectionPrice();
  const externalReferenceCollection =
    useRxCollectionPriceListItemExternalReference();
  const tariffCollection = useRxCollectionPriceListTariff();
  const priceListFacilityCollection = useRxCollectionPriceListFacility();
  const { getCurrentTime } = useServerTime();

  // The facility's own price list comes first; the linked-organisation
  // price list is searched as a fallback when an item isn't priced on the
  // facility-own list. `linkToOrganisationPriceList` (api-price-list)
  // creates an empty facility-own list and sets
  // `linkedOrganisationPriceListId` — so for a facility that has only the
  // org link set up, every price will live on the second list.
  const [priceListIds, setPriceListIds] = useState<string[]>([]);
  useEffect(() => {
    if (!priceListFacilityCollection || !facility?.id) return;
    const sub = priceListFacilityCollection
      .findOne({ selector: { id: facility.id } })
      .$.subscribe((row) => {
        const next = [
          row?.priceListId,
          row?.linkedOrganisationPriceListId,
        ].filter((id): id is string => !!id);
        // Equality-guard so unchanged rxdb emissions don't churn the
        // `emit` callback identity — every downstream memo/effect keyed
        // on it would otherwise re-run on every replication tick.
        setPriceListIds((prev) =>
          prev.length === next.length && prev.every((id, i) => id === next[i])
            ? prev
            : next,
        );
      });
    return () => sub.unsubscribe();
  }, [priceListFacilityCollection, facility?.id]);

  const isPreServiceFacility = facility?.billingMode === BillingMode.PreService;
  const currency = TERRITORY_TO_BILLING_CURRENCY[territory];
  const enabled = !!(
    rxdbBillingEnabled &&
    isPreServiceFacility &&
    invoiceCollection &&
    priceCollection &&
    externalReferenceCollection &&
    tariffCollection &&
    priceListFacilityCollection &&
    facility &&
    currency
  );

  const emit = useCallback(
    async ({
      patientId,
      externalReference,
      scopeId,
      actorId,
      incurredAt,
    }: EmitLineItemArgs) => {
      if (
        !rxdbBillingEnabled ||
        !isPreServiceFacility ||
        !invoiceCollection ||
        !priceCollection ||
        !externalReferenceCollection ||
        !tariffCollection ||
        !priceListFacilityCollection ||
        !facility ||
        !currency
      ) {
        return;
      }

      const currentPriceListIds =
        priceListIds.length > 0
          ? priceListIds
          : await priceListFacilityCollection
              .findOne({ selector: { id: facility.id } })
              .exec()
              .then((row) =>
                [row?.priceListId, row?.linkedOrganisationPriceListId].filter(
                  (id): id is string => !!id,
                ),
              );

      const resolvedPrice =
        currentPriceListIds.length > 0
          ? await lookupPrice({
              origin: externalReference.origin,
              foreignId: externalReference.foreignId,
              priceListIds: currentPriceListIds,
              externalReferenceCollection,
              priceCollection,
              tariffCollection,
            })
          : null;

      if (!resolvedPrice) return;

      const timestamp = getCurrentTime().toISOString();
      const newItem = buildLineItem({
        id: randomUniqueId(),
        externalReference,
        scopeId,
        incurredAt: incurredAt ?? timestamp,
        resolvedPrice,
        actorId,
      });

      await coordinatePendingInvoice(
        `${facility.id}:${patientId}`,
        async () => {
          const existing = await findActivePendingInvoice({
            patientId,
            facilityId: facility.id,
            invoiceCollection,
          });

          if (!existing) {
            await createPendingInvoice({
              id: randomUniqueId(),
              payerId: randomUniqueId(),
              timestamp,
              patientId,
              facilityId: facility.id,
              currency,
              actorId,
              initialLineItems: [
                {
                  ...newItem,
                  quantity: newItem.quantity as number,
                  payer: { type: PayerType.Patient },
                },
              ],
              invoiceCollection,
            });
            return;
          }

          const alreadyPresent = existing.lineItems.find(
            (li) => li.idempotencyKey === newItem.idempotencyKey,
          );
          if (alreadyPresent) return;

          const doc = await invoiceCollection
            .findOne({ selector: { id: existing.id } })
            .exec();
          if (!doc) return;

          // Re-read the live document's lineItems so we don't append to the
          // stale snapshot from findActivePendingInvoice — protects against an
          // interleaved write from another emit landing between the find and
          // this update.
          const liveLineItems = (doc.toJSON() as typeof existing).lineItems;
          const liveExistingItem = liveLineItems.find(
            (li) => li.idempotencyKey === newItem.idempotencyKey,
          );
          if (liveExistingItem) return;

          await doc.incrementalModify((latest) => {
            if (
              latest.lineItems.some(
                (line) => line.idempotencyKey === newItem.idempotencyKey,
              )
            ) {
              return latest;
            }
            const next = Invoice.fromRecord(latest)
              .addLineItem({
                ...newItem,
                quantity: newItem.quantity as number,
                payer: { type: PayerType.Patient },
              })
              .toRecord();
            return {
              ...latest,
              ...next,
              meta: {
                ...next.meta,
                updatedAt: timestamp,
                updatedByUserId: actorId,
              },
            };
          });
        },
      );
    },
    [
      rxdbBillingEnabled,
      isPreServiceFacility,
      facility,
      currency,
      invoiceCollection,
      priceCollection,
      externalReferenceCollection,
      tariffCollection,
      priceListFacilityCollection,
      priceListIds,
      getCurrentTime,
    ],
  );

  if (!enabled) return noopApi;
  return { enabled: true, emit };
};
